import { useEffect, useMemo, useState } from "react";
import MainLayoutWithSideNav from "../../components/Layout/MainLayoutWithSideNav";
import { AsyncStorageKeys, Categories } from "../../Types";
import { getTransactionFilters, globalAny, refreshPage } from "../../utils/Utils";
import CustomizeLoader from "../../components/Loaders/CustomizeLoader";
import defaultStudioAccessBanner from "../../assets/Images/StudioAccess.webp";
import ridevueLogo from "../../assets/Logo/ridevuLogo.svg";
import { useNavigation } from "@react-navigation/native";
import { routeSearchScreen, routeSettings } from "../../Routes";
import PlaylistContainer from "../../components/Layout/PlaylistContainer";
import useGenericContentStore from "../../store/genericContent.store";
import { AppLogger } from "../../utils/AppLogger";
import { getCategories } from "../../services/categoriesService";
import useSearchDataStore from "../../store/useSearchData.store";
import { getPage } from "../../services/pageService";
import Toast from "../../components/Toast";
import ComponentTypeEnum from "../../models/ComponentTypeEnum";
import { infoSubscription } from "../../services/accountService";
import { RetryCommand } from "../../enums/RetryCommand";
import useRetryCommand from "../../hooks/userRetryCommand";
import { StyledLoaderContent } from "./styled";

const packshots = Array.from({ length: 15 }, (_, i) => i + 1);

const defaultItem = [
	{ id: 1, isLandscape: true, packshot: packshots },
	{ id: 2, isLandscape: false, packshot: packshots },
	{ id: 3, isLandscape: false, packshot: packshots },
];

const Home = () => {
	const navigation = useNavigation();

	// generic content store
	const { setNavigateEvent, isRenderHome, setSelectedTabs, setRetryCommand, isNarrow } = useGenericContentStore((state: any) => ({
		setNavigateEvent: state.setNavigateEvent,
		isRenderHome: state.isRenderHome,
		setSelectedTabs: state.setSelectedTabs,
		setRetryCommand: state.setRetryCommand,
		isNarrow: state.isNarrow,
	}));

	// search data store
	const { setLastSearchQuery, setSearchResults, setLastNext } = useSearchDataStore((state: any) => ({
		setLastSearchQuery: state.setLastSearchQuery,
		setSearchResults: state.setSearchResults,
		setLastNext: state.setLastNext,
	}));

	// local state
	const [studioAccessBanner, setStudioAccessBanner] = useState(defaultStudioAccessBanner);
	const [categories, setCategories] = useState<any[]>([]);
	const [isShowLoader, setIsShowLoader] = useState(true);
	const [isAggregate, setIsAggregate] = useState(false);
	const [startIndex, setStartIndex] = useState(5);
	const [isToast, setIsToast] = useState(true);
	const [isMHU, setIsMHU] = useState(false);

	/**
	 * Hides the toast/snackbar notification.
	 */
	const onDismissSnackBar = () => setIsToast(false);

	/**
	 * Handles search action.
	 * - Navigates to the route defined in `routeSearchScreen`.
	 */
	const onSearch = () => {
		//@ts-ignore
		navigation.navigate(routeSearchScreen);
	};

	/**
	 * Handles "Add Screens" button press:
	 * - Switches selected tab to index 3.
	 * - Navigates to the route defined in `routeSettings`.
	 */
	const onPressAddScreens = () => {
		setSelectedTabs(3);
		//@ts-ignore
		navigation.navigate(routeSettings);
	};

	/**
	 * Memoized Home playlist component.
	 * - Renders `PlaylistContainer` with home categories, banner, filters, and state handlers.
	 * - Recomputes only when `categories`, `startIndex`, or `isNarrow` changes.
	 */
	const renderHomePlaylist = useMemo(
		() => (
			<PlaylistContainer
				categories={categories}
				isSeeMore={true}
				location={Categories.home}
				footerBanner={studioAccessBanner}
				transactionalFilters={getTransactionFilters()}
				setIsShowLoader={setIsShowLoader}
				setStartIndex={setStartIndex}
				startIndex={startIndex}
				setIsAggregate={setIsAggregate}
				isHome={true}
			/>
		),
		[categories, startIndex, isNarrow],
	);

	/**
	 * Fetches and initializes home data:
	 * - Subscribes to info updates.
	 * - Resets categories.
	 * - Loads the Studio Access banner (from StudioTwo category pages).
	 * - Loads Home categories with transaction filters.
	 * - Clears navigation, search query, search results, and pagination state.
	 */
	const fetchHomeData = async () => {
		await infoSubscription();
		setCategories([]);
		const controller = new AbortController();
		try {
			const pages: any = await getPage(Categories.studioTwo, controller.signal);
			const [page] = pages.pages;
			const [pageImage] = page.pageImages;
			setStudioAccessBanner(pageImage.imageUrl);
		} catch (err) {
			AppLogger.log(err);
		}
		try {
			let categories: any = (await getCategories(Categories.home, getTransactionFilters(), controller.signal)) ?? [];
			setCategories(categories);
		} catch (err) {
			AppLogger.log(err);
		}
		setNavigateEvent("");
		setLastSearchQuery("");
		setSearchResults([]);
		setLastNext(0);
	};

	/**
	 * When `isRenderHome` becomes true:
	 * - Shows the loader.
	 * - Fetches home data.
	 */
	useEffect(() => {
		if (isRenderHome) {
			setIsShowLoader(true);
			fetchHomeData();
		}
	}, [isRenderHome]);

	/**
	 * On component mount:
	 * - Sets the retry command to "Home".
	 * - Fetches home data.
	 *
	 * On component unmount:
	 * - Clears the categories state.
	 */
	useEffect(() => {
		setRetryCommand(RetryCommand.Home);
		fetchHomeData();
		return () => {
			setCategories([]);
		};
	}, []);

	/**
	 * Tracks how many times an "add screen" action has occurred.
	 * - If the stored count exists and is >= 3, disables the toast.
	 * - Otherwise, increments the count and saves it in localStorage.
	 * - If no count exists, initializes it to 1.
	 * Runs only once on component mount.
	 */
	useEffect(() => {
		const getAddScreensCount = localStorage.getItem(AsyncStorageKeys.addScreenCount);
		if (getAddScreensCount) {
			const count = Number(getAddScreensCount);

			if (count >= 3) {
				setIsToast(false);
				return;
			}

			localStorage.setItem(AsyncStorageKeys.addScreenCount, (count + 1).toString());
			return;
		}
		localStorage.setItem(AsyncStorageKeys.addScreenCount, "1");
	}, []);

	/**
	 * Checks if the "in-car screen" flag is stored in localStorage.
	 * If available and set to "true", enables MHU mode by updating state.
	 * Runs only once on component mount.
	 */
	useEffect(() => {
		const isInCarScreenAvailable = localStorage.getItem(AsyncStorageKeys.isInCarScreen) === "true";
		if (isInCarScreenAvailable) {
			setIsMHU(true);
			return;
		}
		setIsMHU(false);
	}, []);

	useRetryCommand(RetryCommand.Home, refreshPage);

	return (
		<MainLayoutWithSideNav
			searchIcon={true}
			navTitle={globalAny.language.header_home}
			titleLogo={ridevueLogo}
			onPressRightIcon={onSearch}
			isNarrow={isNarrow}
		>
			<StyledLoaderContent isShowLoader={isShowLoader} isNarrow={isNarrow}>
				<CustomizeLoader show={isShowLoader} isHeroBanner={true} isPlaylist={true} data={defaultItem} isHome={true} />
			</StyledLoaderContent>

			{renderHomePlaylist}

			{isToast && !isShowLoader && !isAggregate && isMHU ? (
				<Toast
					visible={true}
					text={globalAny.language.connect_to_other_screens}
					label={globalAny.language.add_screen.toUpperCase()}
					onDismissSnackBar={() => onDismissSnackBar()}
					onPress={() => onPressAddScreens()}
					type={ComponentTypeEnum.Tertiary}
					duration={30000}
				/>
			) : (
				<></>
			)}
		</MainLayoutWithSideNav>
	);
};

export default Home;
